<script>
  import NavigatedViewer from "bpmn-js/lib/NavigatedViewer.js";
  import "bpmn-js/dist/assets/bpmn-js.css";
  import "bpmn-js/dist/assets/diagram-js.css";
  import "bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css";
  import {
    FIT_SCREEN_ICON,
    FULLSCREEN_ICON,
    FULLSCREEN_OFF_ICON,
  } from "@utils/icons.js";
  import { Button } from "paxray-components-svelte";
  import { createEventDispatcher } from "svelte";
  import { onDestroy, onMount, tick } from "svelte";

  export let bpmnXml = "";
  export let markers = [];
  export let hoverElementId = "";
  export let actionsRightOffset = "0";
  export let actionsBottomOffset = "0";
  export let showFullscreen = true;
  export let fullscreen = false;

  const dispatch = createEventDispatcher();
  let viewdiv;
  let viewer;
  let zoomLevel = 100;
  let loadedXml = "";
  let appliedMarkers = [];
  let resizeObserver;
  const markerColors = {
    change_added: "#15803d",
    change_removed: "#b91c1c",
    change_changed: "#a16207",
    change_layout_changed: "#0369a1",
    change_hover: "#1c1c20",
  };
  $: allMarkers = [
    ...markers,
    ...(hoverElementId
      ? [{ id: hoverElementId, className: "change_hover" }]
      : []),
  ];
  $: markerKey = allMarkers
    .map((marker) => `${marker.id}:${marker.className}`)
    .join("|");

  function initViewer() {
    viewer = new NavigatedViewer({
      container: viewdiv,
    });
  }

  async function fitDiagram() {
    if (!viewer) return;
    await tick();
    requestAnimationFrame(() => {
      const canvas = viewer.get("canvas");
      canvas.resized();
      canvas.zoom("fit-viewport", true);
    });
  }

  async function loadXml(xml) {
    if (!viewer || !xml || xml === loadedXml) return;
    try {
      await viewer.importXML(xml);
      loadedXml = xml;
      applyMarkers();
      fitDiagram();
    } catch (err) {
      console.error("Failed to render BPMN preview:", err);
    }
  }

  function applyMarkers() {
    if (!viewer) return;
    const canvas = viewer.get("canvas");
    const elementRegistry = viewer.get("elementRegistry");

    appliedMarkers.forEach((marker) => {
      if (elementRegistry.get(marker.id)) {
        resetConnectionMarker(elementRegistry, marker.id);
        canvas.removeMarker(marker.id, marker.className);
      }
    });

    allMarkers.forEach((marker) => {
      if (elementRegistry.get(marker.id)) {
        canvas.addMarker(marker.id, marker.className);
        colorConnectionMarker(elementRegistry, marker);
      }
    });

    appliedMarkers = [...allMarkers];
  }

  function colorConnectionMarker(elementRegistry, marker) {
    const color = markerColors[marker.className];
    const element = elementRegistry.get(marker.id);
    if (!color || !element?.waypoints) return;

    const gfx = elementRegistry.getGraphics(marker.id);
    const svg = gfx?.ownerSVGElement;
    if (!gfx || !svg) return;

    gfx
      .querySelectorAll("[marker-start], [marker-end]")
      .forEach((connectionPath) => {
        ["marker-start", "marker-end"].forEach((attribute) => {
          const markerReference = connectionPath.getAttribute(attribute);
          const markerId = markerReference?.match(/url\(#([^)]+)\)/)?.[1];
          const markerElement = markerId && svg.querySelector(`#${markerId}`);
          if (!markerElement) return;
          const originalAttribute = `data-original-${attribute}`;
          const originalReference =
            connectionPath.getAttribute(originalAttribute) || markerReference;
          const originalMarkerId =
            originalReference?.match(/url\(#([^)]+)\)/)?.[1];
          const coloredMarkerId = `${originalMarkerId}-${marker.className}`;
          let coloredMarker = svg.querySelector(`#${coloredMarkerId}`);
          if (!coloredMarker) {
            coloredMarker = markerElement.cloneNode(true);
            coloredMarker.id = coloredMarkerId;
            markerElement.parentNode.appendChild(coloredMarker);
          }
          coloredMarker
            .querySelectorAll("path, circle, polygon")
            .forEach((shape) => {
              shape.setAttribute("fill", color);
              shape.setAttribute("stroke", color);
            });
          connectionPath.setAttribute(originalAttribute, originalReference);
          connectionPath.setAttribute(attribute, `url(#${coloredMarkerId})`);
        });
      });
  }

  function resetConnectionMarker(elementRegistry, id) {
    const gfx = elementRegistry.getGraphics(id);
    if (!gfx) return;

    gfx
      .querySelectorAll(
        "[data-original-marker-start], [data-original-marker-end]",
      )
      .forEach((connectionPath) => {
        ["marker-start", "marker-end"].forEach((attribute) => {
          const originalAttribute = `data-original-${attribute}`;
          const originalReference =
            connectionPath.getAttribute(originalAttribute);
          if (!originalReference) return;
          connectionPath.setAttribute(attribute, originalReference);
          connectionPath.removeAttribute(originalAttribute);
        });
      });
  }

  export function resetZoom() {
    zoomLevel = 100;
    fitDiagram();
  }

  export function zoomIn(value) {
    if (!viewer) return value;
    const zoomScroll = viewer.get("zoomScroll");
    zoomScroll.stepZoom(1);
    return Number(value) + 1;
  }

  export function zoomOut(value) {
    if (!viewer) return value;
    const zoomScroll = viewer.get("zoomScroll");
    zoomScroll.stepZoom(-1);
    return Number(value) - 1;
  }

  function downloadUtf8(content, filename, mimeType) {
    const blob = new Blob([new TextEncoder().encode(content)], {
      type: `${mimeType};charset=utf-8`,
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  export async function downloadSVG(filename = "change-analysis.svg") {
    const sourceSvg = viewdiv?.querySelector("svg");
    if (!sourceSvg) return;

    const clonedSvg = sourceSvg.cloneNode(true);
    const sourceElements = [sourceSvg, ...sourceSvg.querySelectorAll("*")];
    const clonedElements = [clonedSvg, ...clonedSvg.querySelectorAll("*")];
    const inlineProperties = [
      "color",
      "fill",
      "fill-opacity",
      "font-family",
      "font-size",
      "font-style",
      "font-weight",
      "marker-end",
      "marker-start",
      "opacity",
      "stroke",
      "stroke-dasharray",
      "stroke-linecap",
      "stroke-linejoin",
      "stroke-opacity",
      "stroke-width",
      "text-anchor",
    ];

    sourceElements.forEach((element, index) => {
      const clonedElement = clonedElements[index];
      if (!clonedElement || !(element instanceof Element)) return;
      const computedStyle = getComputedStyle(element);
      inlineProperties.forEach((property) => {
        const value = computedStyle.getPropertyValue(property);
        if (value) clonedElement.style.setProperty(property, value);
      });
    });

    clonedSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const viewBox = clonedSvg.getAttribute("viewBox")?.split(/\s+/).map(Number);
    if (viewBox?.length === 4) {
      clonedSvg.setAttribute("width", String(viewBox[2]));
      clonedSvg.setAttribute("height", String(viewBox[3]));
    }
    downloadUtf8(
      new XMLSerializer().serializeToString(clonedSvg),
      filename,
      "image/svg+xml",
    );
  }

  $: if (viewer && bpmnXml) loadXml(bpmnXml);
  $: if (viewer && bpmnXml && markerKey !== undefined) applyMarkers();

  onMount(() => {
    initViewer();
    resizeObserver = new ResizeObserver(() => {
      fitDiagram();
    });
    if (viewdiv) resizeObserver.observe(viewdiv);
  });

  onDestroy(() => {
    if (resizeObserver) resizeObserver.disconnect();
    if (viewer) viewer.destroy();
  });
</script>

<div class="bpmn_preview" class:has_hover={hoverElementId}>
  <div class="bpmn_canvas" bind:this={viewdiv} />
  <div
    class="preview_actions"
    style="right: {actionsRightOffset}; bottom: {actionsBottomOffset};"
  >
    <div class="preview_zoom">
      <Button
        kind="plus-minus"
        text="Zoom"
        bind:value={zoomLevel}
        showValue={false}
        unit=""
        reset={resetZoom}
        increment={zoomIn}
        decrement={zoomOut}
      />
    </div>
    <Button
      icon={FIT_SCREEN_ICON}
      iconDescription="Fit model to view"
      on:click={resetZoom}
    />
    {#if showFullscreen}
      <Button
        kind="secondary"
        icon={fullscreen ? FULLSCREEN_OFF_ICON : FULLSCREEN_ICON}
        iconDescription={fullscreen ? "Exit fullscreen" : "Fullscreen"}
        on:click={() => {
          fullscreen = !fullscreen;
        }}
      />
    {/if}
  </div>
</div>

<style>
  .bpmn_preview {
    background-color: var(--pax-white);
    background-image: radial-gradient(#d6d9de 1px, transparent 1px);
    background-size: 16px 16px;
    height: 100%;
    overflow: hidden;
    position: relative;
    width: 100%;
  }

  .bpmn_canvas {
    background-color: transparent;
    background-image: radial-gradient(#d6d9de 1px, transparent 1px);
    background-size: 16px 16px;
    height: 100%;
    width: 100%;
  }

  :global(.bpmn_canvas .djs-container),
  :global(.bpmn_canvas .djs-container > svg) {
    background-color: transparent !important;
    background-image: radial-gradient(#d6d9de 1px, transparent 1px) !important;
    background-size: 16px 16px !important;
  }

  :global(.bpmn_preview svg:focus) {
    outline: none;
  }

  :global(.bpmn_preview .bjs-powered-by) {
    display: none !important;
  }

  :global(.bpmn_preview .djs-element) {
    transition: opacity 140ms ease-in-out;
  }

  :global(.bpmn_preview.has_hover .djs-element:not(.change_hover)) {
    opacity: 0.24;
  }

  :global(.bpmn_preview.has_hover .djs-element.change_hover) {
    opacity: 1;
  }

  :global(.bpmn_preview .change_added .djs-visual > :first-child) {
    fill: #b7e3bd !important;
    stroke: #15803d !important;
    stroke-width: 3px !important;
  }

  :global(.bpmn_preview .djs-connection.change_added .djs-visual path),
  :global(.bpmn_preview .djs-connection.change_added .djs-visual polyline) {
    fill: none !important;
    stroke: #15803d !important;
    stroke-width: 2px !important;
  }

  :global(.bpmn_preview .change_removed .djs-visual > :first-child) {
    fill: #e6a7ad !important;
    stroke: #b91c1c !important;
    stroke-width: 3px !important;
  }

  :global(.bpmn_preview .djs-connection.change_removed .djs-visual path),
  :global(.bpmn_preview .djs-connection.change_removed .djs-visual polyline) {
    fill: none !important;
    stroke: #b91c1c !important;
    stroke-width: 2px !important;
  }

  :global(.bpmn_preview .change_changed .djs-visual > :first-child) {
    fill: #e5e8a8 !important;
    stroke: #a16207 !important;
    stroke-width: 3px !important;
  }

  :global(.bpmn_preview .djs-connection.change_changed .djs-visual path),
  :global(.bpmn_preview .djs-connection.change_changed .djs-visual polyline) {
    fill: none !important;
    stroke: #a16207 !important;
    stroke-width: 2px !important;
  }

  :global(.bpmn_preview .change_layout_changed .djs-visual > :first-child) {
    fill: #abcfe8 !important;
    stroke: #0369a1 !important;
    stroke-width: 3px !important;
  }

  :global(.bpmn_preview .djs-connection.change_layout_changed .djs-visual path),
  :global(
      .bpmn_preview .djs-connection.change_layout_changed .djs-visual polyline
    ) {
    fill: none !important;
    stroke: #0369a1 !important;
    stroke-width: 2px !important;
  }

  :global(.bpmn_preview .change_hover .djs-visual > :first-child) {
    stroke: var(--pax-black) !important;
    stroke-width: 5px !important;
  }

  :global(.bpmn_preview .djs-connection.change_hover .djs-visual path),
  :global(.bpmn_preview .djs-connection.change_hover .djs-visual polyline) {
    fill: none !important;
    stroke: var(--pax-black) !important;
    stroke-width: 3px !important;
  }

  .preview_actions {
    align-items: center;
    display: flex;
    gap: 0.5rem;
    position: absolute;
    z-index: 2;
  }

  .preview_zoom :global(.button_wrapper) {
    height: 2.5rem;
  }

  .preview_zoom :global(.button) {
    height: 2rem;
    width: 2rem;
  }
</style>
